import { Clipboard } from '@angular/cdk/clipboard';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, Inject, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { JoinCompanyService } from '../../../services/join-company.service';
import { CompanyService } from '../../../state-services/company.service';
import { selectCompanyData } from '../../../stores/company/company.selector';

type InviteMemberModalData = {
  companyId?: string;
  companyName?: string;
};

@Component({
  selector: 'app-invite-member-modal',
  templateUrl: './invite-member-modal.component.html',
  styleUrls: ['./invite-member-modal.component.scss'],
  standalone: false,
})
export class InviteMemberModalComponent {
  @Input() open: boolean = false;
  errorMessage: string = '';
  isCopied: boolean = false;
  private companyId: string = '';
  private companyName: string = '';
  link: string = window.location.host + '/join/';
  form: FormGroup = this.formBuilder.nonNullable.group({
    email: this.formBuilder.control(
      { value: '', disabled: false },
      {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.email,
          Validators.maxLength(100),
        ],
      },
    ),
  });

  constructor(
    public dialogRef: DialogRef<any>,
    @Inject(DIALOG_DATA) public data: InviteMemberModalData | null,
    private clipboard: Clipboard,
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private store: Store,
    private joinCompanyService: JoinCompanyService,
    private dashboardService: DashboardService,
  ) {
    if (this.data?.companyId) {
      this.setCompanyContext(this.data.companyId, this.data.companyName ?? '');
      return;
    }

    this.store
      .select(selectCompanyData)
      .pipe(take(1))
      .subscribe((company) => {
        if (!company.id) {
          return;
        }
        this.setCompanyContext(company.id, company.name ?? '');
      });
  }

  abortModal(): void {
    this.dialogRef.close();
  }

  finishModal(): void {
    this.dialogRef.close({});
  }

  sendInvitation() {
    if (!this.companyId) {
      this.errorMessage = 'Unable to resolve company for invitation.';
      return;
    }

    this.companyService
      .sendInviteViaEmail(this.companyId, this.form.value.email)
      .subscribe({
        next: () => {
          this.dashboardService.openSuccessResponseBanner(
            'Email was successfully sent',
          );
        },
        error: () => {
          this.dashboardService.openErrorResponseBanner(
            'Something went wrong, try again',
          );
        },
        complete: () => {
          this.finishModal();
        },
      });
  }

  copyLink() {
    this.clipboard.copy(this.link);
    this.showIsCopiedBanner(7500);
  }

  showIsCopiedBanner(time: number) {
    this.isCopied = true;
    setTimeout(() => {
      this.isCopied = false;
    }, time);
  }

  private setCompanyContext(companyId: string, companyName: string): void {
    this.companyId = companyId;
    this.companyName = companyName;
    this.buildInvitationLink();
  }

  private buildInvitationLink(): void {
    if (!this.companyId) {
      return;
    }

    this.companyService
      .getInviteLinkAsBase64(this.companyId)
      .pipe(take(1))
      .subscribe((inviteLink) => {
        let nextLink =
          window.location.host +
          '/join/' +
          this.joinCompanyService.makeUrlFriendlyBase64Text(inviteLink);
        if (this.companyName) {
          nextLink += '?name=' + encodeURI(this.companyName);
        }
        this.link = nextLink;
      });
  }
}
